import { AddIcon } from "@chakra-ui/icons";
import {
	Box,
	Badge,
	Button,
	Icon,
	Switch,
	Image,
	Text,
	Flex,
} from "@chakra-ui/react";
import { useState } from "react";
import { apiRequest } from "utils/api";

export interface IntegrationCardProps {
	controller: string;
	name: string;
	description: string;
	logo: string;
	connected: boolean;
}

export function IntegrationCard({
	name,
	description,
	logo,
	connected,
	controller,
}: IntegrationCardProps) {
	const [isConnected, setIsConnected] = useState(connected);
	const [isLoading, setIsLoading] = useState(false);

	async function connect() {
		const response = await apiRequest(`/${controller}/connect`, {}, "post");
		console.log(response.url);
		window.location.href = response.url;
	}

	async function disconnect() {
		setIsLoading(true);
		await apiRequest(`/${controller}/disconnect`, {}, "post");
		setIsConnected(false);
		setIsLoading(false);
	}

	async function handleToggle(isConnected: boolean) {
		if (isConnected) {
			await disconnect();
		} else {
			await connect();
		}
	}

	return (
		<Box
			key={name}
			padding="24px"
			borderRadius="16px"
			border="1px solid #E2E2E2"
			display="flex"
			flexDirection="column"
			minHeight="220px"
			transition="all 0.2s"
			_hover={{
				boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
				borderColor: "#D1D1D1",
			}}
		>
			<Box flex="1">
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					mb="16px"
				>
					<Image
						src={logo}
						borderRadius="8px"
						boxSize="40px"
						alt={name}
						objectFit="contain"
					/>
					{isConnected && (
						<Badge
							background="#ECF9ED"
							color="green.500"
							fontSize="12px"
							fontWeight="600"
							px="8px"
							py="2px"
							borderRadius="full"
						>
							Connected
						</Badge>
					)}
				</Box>
				<Text fontWeight="600" fontSize="16px" mb="8px">
					{name}
				</Text>
				<Text fontSize="14px" color="gray.600">
					{description}
				</Text>
			</Box>

			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mt="16px"
				pt="16px"
				borderTop="1px solid #E2E2E2"
			>
				<Flex gap={4}>
					<Button
						isLoading={isLoading}
						variant={isConnected ? "link" : "outline"}
						size="sm"
						display="flex"
						gap="4px"
						onClick={() => handleToggle(isConnected)}
						color={isConnected ? "red.500" : "blue.500"}
					>
						{isConnected ? (
							"Disconnect"
						) : (
							<>
								<Icon as={AddIcon} />
								Connect
							</>
						)}
					</Button>
					{isConnected && name === "Xero" && (
						<Button
							isDisabled={isLoading}
							variant="link"
							size="sm"
							onClick={() => handleToggle(false)}
						>
							Add another account
						</Button>
					)}
				</Flex>
				{isConnected && (
					<Box display="flex" alignItems="center" gap="8px">
						<Switch
							isDisabled={isLoading}
							size="sm"
							isChecked={isConnected}
							colorScheme="green"
							onChange={() => handleToggle(true)}
						/>
						<Text fontSize="14px" color="gray.600">
							Active
						</Text>
					</Box>
				)}
			</Box>
		</Box>
	);
}
